import * as React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';

import { color, fontSize, radius, spacing } from 'common/lib/theme/dynamic';
import { formatShortcut } from 'lib/keyboardShortcuts';

const Root = styled.kbd`
  color: ${({ theme }) => theme.color.textLight};
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.small};
`;

type Props = {
  /**
   * The shortcut keys to render. Use "mod" for modifier key, it will be changed depending on user's platform.
   *
   * Accepts Mousetrap-compatible keys.
   *
   * @example
   * <KeyboardShortcut keys={['mod+k']} />
   * <KeyboardShortcut keys={['ctrl+k']} />
   */
  keys: string[];
  className?: string;
};

const KeyboardShortcut = ({ keys, className }: Props) => {
  const arrayText = useMemo(() => (keys.length === 1 ? keys[0].split(/[+ ]/) : keys), [keys]);
  const parsedText = useMemo(() => formatShortcut(arrayText), [arrayText]);

  return (
    <Root className={className}>
      {parsedText.map((t, index) => (
        <span key={`${t}-${index}`}>{t}</span>
      ))}
    </Root>
  );
};

/** Variant of {@link KeyboardShortcut} that displays the shortcut keys in small boxes. */
export const BlockyKeyboardShortcut = styled(KeyboardShortcut)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xxxsmall};

  > span {
    color: ${color.pureWhite};
    border: 1px solid ${color.gray9};
    border-radius: ${radius.small};
    min-width: 20px;
    max-height: 20px;
    text-transform: uppercase;
    padding: 0 ${spacing.xxsmall};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${fontSize.xsmall};
  }
`;

export default KeyboardShortcut;
